import MarginRateStyledInput from '@/components/styled/MarginRateStyledInput';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import { Control } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import EstimateAdditionalMarginFields from './EstimateAdditionalMarginFields';
import AgencyOverlayPlaceholderField from './AgencyOverlayPlaceholderField';
import clsx from 'clsx';

interface EstimateAgencyCardsProps {
  control: Control<EstimateFormData>;
  allowAgencyMarginRate?: boolean;
  allowAgencyAdditionalMargin?: boolean;
  /** TO view: disabled indicators. Agency link: editable overlay fields. */
  readOnly?: boolean;
  /** Muted labels when the TO previews agency-owned fields. */
  muted?: boolean;
  /** TO view: show layout without agency margin values. */
  hideValues?: boolean;
}

/**
 * Margin-rate and/or additional-margin block for the agency link (editable) or the
 * TO view (read-only indicators that the agency will fill in).
 */
const EstimateAgencyCards = ({
  control,
  allowAgencyMarginRate = false,
  allowAgencyAdditionalMargin = false,
  readOnly = false,
  muted = false,
  hideValues = false,
}: EstimateAgencyCardsProps) => {
  const { t } = useTranslation();

  if (!allowAgencyMarginRate && !allowAgencyAdditionalMargin) return null;

  const showBoth = allowAgencyMarginRate && allowAgencyAdditionalMargin;
  const cardTitle = showBoth
    ? undefined
    : allowAgencyMarginRate
      ? t('estimates.agencyView.marginRate.generalTitle')
      : t('estimates.agencyView.additionalMargin.title');
  const mutedTextClass = muted ? 'text-gray-500' : 'text-black';
  const mutedLabelClass = muted ? 'text-gray-500' : 'text-gray-700';

  return (
    <Card>
      {cardTitle && (
        <CardHeader>
          <CardTitle>
            <h1 className={clsx('text-xl font-medium', mutedTextClass)}>
              {cardTitle}
            </h1>
          </CardTitle>
        </CardHeader>
      )}
      <CardContent
        className={clsx(
          'flex flex-col gap-4 md:flex-row md:items-start',
          !cardTitle && 'pt-6',
        )}
      >
        {allowAgencyMarginRate && (
          <div className="flex flex-1 flex-col gap-2">
            {showBoth && (
              <Label className={mutedLabelClass}>
                {t('estimates.agencyView.marginRate.generalTitle')}
              </Label>
            )}
            {hideValues ? (
              <AgencyOverlayPlaceholderField muted={muted} />
            ) : (
              <div className="flex h-10 items-center">
                <MarginRateStyledInput
                  control={control}
                  name="agencyMarginRate"
                  disabled={readOnly}
                  placeholder="%"
                  className="w-full gap-0"
                  titleClassName={muted ? 'text-gray-500' : undefined}
                />
              </div>
            )}
          </div>
        )}
        {allowAgencyAdditionalMargin && (
          <EstimateAdditionalMarginFields
            control={control}
            disabled={readOnly}
            muted={muted}
            hideValues={hideValues}
            showSectionLabel={showBoth}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default EstimateAgencyCards;
